import {
  Button2,
  Switch,
  SwitchSize,
  useAppForm,
  useNotifications,
  useFormValidators,
  cn,
} from '@valuecase/ui-components'
import { useRef, useCallback, useState, useEffect, useMemo } from 'react'
import { useCompanyVisualSettings } from './hooks/useCompanyVisualSettings'
import { useDeleteUploadedImage } from './hooks/useDeleteUploadedImage'
import { SettingsInfo } from './SettingsInfo'
import { useTrackEvent } from '../../mixpanel/useTrackEvent'
import { useS3Upload } from '@/api/s3'

export const CustomSpacePreviewSetting = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const { success } = useNotifications()
  const { upload } = useS3Upload({ publicBucket: true })
  const { show } = useDeleteUploadedImage()
  const { trackEvent } = useTrackEvent()

  const { companySettings, updateCompanySettings } = useCompanyVisualSettings({
    onUpdateSuccess: () => {
      success('Preview settings updated')
    },
  })

  const [isEnabled, setIsEnabled] = useState(false)
  const [imageErrorMessage, setImageErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (companySettings) {
      const hasImage = !!companySettings.spacePreviewImageUrl
      const hasTitle = !!companySettings.spacePreviewTitle
      setIsEnabled(hasImage || hasTitle)
    }
  }, [companySettings])

  const handleUploadClick = useCallback(() => {
    if (fileInputRef?.current) {
      fileInputRef.current.click()
    }
  }, [])

  const handleUploadPreviewImage = useCallback(
    async (file: File) => {
      setImageErrorMessage(null)

      if (file.size > 2 * 1024 * 1024) {
        setImageErrorMessage('File size exceeds 2MB')
        return
      }

      const { bucketUrl, s3ObjectKey } = await upload(file)
      trackEvent({
        event: 'settings-preview-upload_finished',
        eventProperties: {
          indexPageName: 'Company Settings',
        },
      })

      updateCompanySettings({ spacePreviewImageUrl: `${bucketUrl}/${s3ObjectKey}` })
    },
    [upload, trackEvent, updateCompanySettings],
  )

  const deletePreviewImage = useCallback(() => {
    show(() => updateCompanySettings({ spacePreviewImageUrl: null }), 'spacePreviewImage')
    setImageErrorMessage(null)
  }, [show, updateCompanySettings])

  const handleFileChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      e.preventDefault()
      const file = (e.target as HTMLInputElement)?.files?.[0]
      if (file) {
        handleUploadPreviewImage(file)
      }
    },
    [handleUploadPreviewImage],
  )

  const handleSwitchChange = useCallback(
    (checked: boolean) => {
      setIsEnabled(checked)
      if (!checked) {
        // If toggle is turned off, clear both image and title
        updateCompanySettings({
          spacePreviewImageUrl: null,
          spacePreviewTitle: null,
        })
        trackEvent({
          event: 'settings-preview-disabled',
          eventProperties: {
            indexPageName: 'Company Settings',
          },
        })
      } else {
        trackEvent({
          event: 'settings-preview-enabled',
          eventProperties: {
            indexPageName: 'Company Settings',
          },
        })
      }
    },
    [updateCompanySettings, trackEvent],
  )

  const defaultValues = useMemo<{
    title: string
  }>(
    () => ({
      title: companySettings?.spacePreviewTitle ?? '',
    }),
    [companySettings?.spacePreviewTitle],
  )

  const { maxLength } = useFormValidators()

  const titleForm = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      updateCompanySettings({
        spacePreviewTitle: value.title || null,
      })
    },
  })

  return (
    <div className='flex flex-col w-full gap-6'>
      <div className='flex justify-between w-full items-start'>
        <div className='flex flex-col gap-3'>
          <SettingsInfo
            title='Custom Space Preview'
            description='Set your own title and cover image for your Spaces when sharing links on LinkedIn, Slack, and more. This helps ensure your Spaces always look professional and on-brand in link previews.'
          />
          <div className='flex flex-col gap-2'>
            <p className='text-sm text-grey-s5 font-medium'>Recommended settings:</p>
            <ul className='list-disc pl-5 text-sm text-grey-s5'>
              <li>Image: 1200 × 630 px (JPG/PNG, under 1MB), Ratio: 1.91:1</li>
              <li>Title: Recommended length between 40–60 characters</li>
            </ul>
          </div>
        </div>

        <div className='flex flex-col gap-2 items-end'>
          <Switch
            checked={isEnabled}
            onChange={handleSwitchChange}
            shouldUseHoverColor={true}
            size={SwitchSize.NORMAL}
          />
          {isEnabled && (
            <div className='flex flex-col gap-4 bg-white rounded-md border border-grey-s2 p-3 pt-2'>
              <div className='flex flex-col gap-1'>
                <div className='flex justify-between items-center'>
                  <h4 className='text-sm font-medium'>Image</h4>
                  <div className='flex gap-2'>
                    <input
                      ref={fileInputRef}
                      // Forces re-render to reset the file input
                      key={companySettings?.spacePreviewImageUrl}
                      type='file'
                      className='hidden'
                      multiple={false}
                      accept='image/png, image/jpeg'
                      onInput={handleFileChange}
                    />
                    <Button2
                      variant='plain'
                      size={'small'}
                      trailingIcon='cloud-upload-outline'
                      onClick={() => {
                        trackEvent({
                          event: 'settings-preview-change_click',
                          eventProperties: {
                            indexPageName: 'Company Settings',
                          },
                        })
                        handleUploadClick()
                      }}
                    />
                    {companySettings?.spacePreviewImageUrl && (
                      <Button2
                        size={'small'}
                        variant='plain'
                        leadingIcon='trash-outline'
                        onClick={() => {
                          trackEvent({
                            event: 'settings-preview-remove',
                            eventProperties: {
                              indexPageName: 'Company Settings',
                            },
                          })
                          deletePreviewImage()
                        }}
                      />
                    )}
                  </div>
                </div>
                <img
                  src={
                    companySettings?.spacePreviewImageUrl ||
                    'https://valuecase-static-assets.s3.eu-central-1.amazonaws.com/share_space_preview.png'
                  }
                  alt='Preview'
                  className='w-[274px] rounded-lg'
                />
                <span
                  className={cn('text-xs text-grey-s4', { 'text-warning-s5': imageErrorMessage })}
                >
                  {imageErrorMessage || 'Max. 2MB'}
                </span>
              </div>

              <div className='flex flex-col gap-2'>
                <span className='text-sm font-medium'>Title</span>
                <div className='flex gap-2'>
                  <titleForm.AppField
                    name='title'
                    validators={{
                      onChange: maxLength(60),
                    }}
                    listeners={{
                      onBlur: () => {
                        trackEvent({
                          event: 'settings-preview-title-update',
                          eventProperties: {
                            indexPageName: 'Company Settings',
                          },
                        })
                        titleForm.handleSubmit()
                      },
                    }}
                  >
                    {(field) => (
                      <field.InputField
                        placeholder='Welcome to this shared space!'
                        className='w-full'
                        helpText='Max. 60 characters'
                        maxCount={60}
                        showCount
                      />
                    )}
                  </titleForm.AppField>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
